import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import * as moment from "moment";
import DeleteModal from "../../../Components/Common/DeleteModal";
import UserCreationModal from "./UserCreationModal";
import { Card, CardBody, Col, Container, CardHeader, Row } from "reactstrap";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import TableContainer from "../../../Components/Common/TableContainer";
import Loader from "../../../Components/Common/Loader";
import { useProfile } from "../../../Components/Hooks/UserHooks";

import {
  getUsers as onGetUsers,
  deleteUser as onDeleteUser,
} from "../../../slices/user/thunk";

const selectUserState = createSelector(
  (state) => state.User,
  (user) => ({
    users: user.users || [],
    isUserSuccess: user.isUserSuccess,
    isLoading: user.isLoading,
    error: user.error,
  })
);

const createCommonColumn = (header, accessorKey, enableFilter = false, customCell = null) => ({
  header,
  accessorKey,
  enableColumnFilter: enableFilter,
  headerStyle: { textAlign: "center" },
  cell: customCell || ((cell) => <div className="text-center">{cell.getValue()}</div>),
});

const createStatusColumn = () => ({
  header: "Status",
  accessorKey: "id",
  enableColumnFilter: false,
  headerStyle: { textAlign: "center" },
  cell: (cell) => {
    const row = cell.row.original;
    const isActive = row.is_active;
    const isSuperuser = row.is_superuser;
    const isStaff = row.is_staff;

    return (
      <div className="d-flex align-items-center justify-content-center gap-2">
        {isActive && (
          <span className="badge bg-success d-flex align-items-center p-2 rounded">
            <i className="ri-checkbox-circle-line me-1 fs-5"></i>
            Active
          </span>
        )}
        {!isActive && (
          <span className="badge bg-danger d-flex align-items-center p-2 rounded">
            <i className="ri-close-circle-line me-1 fs-5"></i>
            Inactive
          </span>
        )}
        {isSuperuser && (
          <span className="badge bg-warning d-flex align-items-center p-2 rounded">
            <i className="ri-admin-line me-1 fs-5"></i>
            Superuser
          </span>
        )}
        {isStaff && !isSuperuser && (
          <span className="badge bg-info d-flex align-items-center p-2 rounded">
            <i className="ri-user-settings-line me-1 fs-5"></i>
            Staff
          </span>
        )}
      </div>
    );
  },
});

// Action column
const createActionColumn = (hasDeleteRight, onClickDelete) => ({
  id: "actions",
  enableColumnFilter: false,
  header: "",
  size: 60,
  headerStyle: { textAlign: "center" },
  cell: (cellProps) => {
    return (
      <div className="text-center">
        <ul className="list-inline d-flex justify-content-center align-items-center gap-2 mb-0">
          {hasDeleteRight && (
            <li className="list-inline-item">
              <button
                className="btn btn-sm btn-icon btn-ghost-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  onClickDelete(cellProps.row.original);
                }}
                title="Delete User"
              >
                <i className="ri-delete-bin-5-fill fs-16"></i>
              </button>
            </li>
          )}
        </ul>
      </div>
    );
  },
});

const UserManagement = () => {
  const [deleteModal, setDeleteModal] = useState(false);
  const [createModal, setCreateModal] = useState(false); // Add this state
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userProfile, isAdmin, isEditor } = useProfile();

  const { users, isUserSuccess, isLoading, error } = useSelector(selectUserState);

  useEffect(() => {
    const handleResize = () => {
      setPageSize(window.innerWidth <= 480 ? 5 : 10);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    dispatch(onGetUsers());
  }, [dispatch]);

  const formatDate = (date) => {
    if (!date) return "No data";
    return moment(new Date(date)).format("DD MMM Y");
  };

  const formatDateTime = (date) => {
    if (!date) return "No data";
    return moment(new Date(date)).format("DD MMM Y, HH:mm");
  };

  const formatGroups = (groups) => {
    if (!groups) return "No groups";
    if (typeof groups === "string") return groups;
    if (Array.isArray(groups)) return groups.join(", ");
    return "No groups";
  };

  const handleCreateUserClick = () => {
    setCreateModal(true);
  };

  const toggleCreateModal = () => {
    setCreateModal(!createModal);
  };

  const onClickDelete = useCallback((user) => {
    setSelectedUser(user);
    setDeleteModal(true);
  }, []);

  const handleRowClick = useCallback((row) => {
    navigate(`/admin/panel/users/${row.original.id}`);
  }, [navigate]);

  const handleDeleteUser = useCallback(async () => {
    if (selectedUser) {
      setIsDeleting(true);
      setDeleteError(null);
      try {
        await dispatch(onDeleteUser(selectedUser.id)).unwrap();
        setDeleteModal(false);
        setSelectedUser(null);
        dispatch(onGetUsers());
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "Failed to delete user";
        setDeleteError(errorMessage);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [dispatch, selectedUser]);

  const columns = useMemo(() => [
    createCommonColumn("Username", "username", true, (cell) => (
      <div className="text-center">
        <span className="fw-medium text-primary">{cell.getValue()}</span>
      </div>
    )),
    createCommonColumn("Name", "first_name", true, (cell) => {
      const row = cell.row.original;
      const fullName = `${row.first_name || ""} ${row.last_name || ""}`.trim();
      return (
        <div className="text-center">
          <span className="text-muted">{fullName || "Not provided"}</span>
        </div>
      );
    }),
    createCommonColumn("Email", "email", true, (cell) => (
      <div className="text-center">
        <span className="text-muted">{cell.getValue() || "No email"}</span>
      </div>
    )),
    createCommonColumn("Groups", "groups", true, (cell) => (
      <div className="text-center">
        <span className="badge bg-soft-secondary text-secondary">
          {formatGroups(cell.getValue())}
        </span>
      </div>
    )),
    createCommonColumn("Date Joined", "date_joined", false, (cell) => (
      <div className="text-center text-muted small">
        {formatDate(cell.getValue())}
      </div>
    )),
    createCommonColumn("Last Login", "last_login", false, (cell) => (
      <div className="text-center text-muted small">
        {formatDateTime(cell.getValue())}
      </div>
    )),
    createStatusColumn(),
    createActionColumn(
      isAdmin || isEditor,
      onClickDelete
    ),
  ], [isAdmin, isEditor, onClickDelete]);

  return (
    <div className="page-content">
      <UserCreationModal
        isOpen={createModal}
        toggle={toggleCreateModal}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteUser}
        onCloseClick={() => {
          setDeleteModal(false);
          setSelectedUser(null);
          setDeleteError(null);
        }}
        isDeleting={isDeleting}
        error={deleteError}
      />

      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card id="userList" className="shadow-sm">
              <CardHeader className="border-0 bg-light">
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="card-title mb-0">User Management</h5>
                  {(isAdmin || isEditor) && (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleCreateUserClick}
                    >
                      <i className="ri-add-line align-bottom me-1"></i> Create
                      User
                    </button>
                  )}
                </div>
              </CardHeader>

              <CardBody>
                {isLoading ? (
                  <Loader />
                ) : isUserSuccess ? (
                  users && users.length > 0 ? (
                    <TableContainer
                      columns={columns}
                      data={users}
                      customPageSize={pageSize}
                      isGlobalFilter={true}
                      divClass="table-responsive table-card mb-1"
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light text-muted text-uppercase text-center"
                      SearchPlaceholder="Search users..."
                      handleRowClick={handleRowClick}
                      customTableRowProps={(row) => ({
                        className: "cursor-pointer",
                        style: { cursor: "pointer" }
                      })}
                      initialState={{
                        columnVisibility: {},
                        sorting: [{ id: "username", desc: false }],
                      }}
                    />
                  ) : (
                    <div className="text-center py-5">
                      <div className="avatar-md mx-auto mb-4">
                        <div className="avatar-title rounded-circle bg-light text-primary fs-24">
                          <i className="ri-user-search-line"></i>
                        </div>
                      </div>
                      <h5>No Users Found</h5>
                      <p className="text-muted">
                        No users have been created yet.
                      </p>
                      {(isAdmin || isEditor) && (
                        <button
                          className="btn btn-primary"
                          onClick={handleCreateUserClick}
                        >
                          <i className="ri-add-line me-1"></i>
                          Create First User
                        </button>
                      )}
                    </div>
                  )
                ) : (
                  <div className="text-center mt-5">
                    <p className="text-muted">
                      {error || "Error loading users. Please try again."}
                    </p>
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => dispatch(onGetUsers())}
                    >
                      <i className="ri-refresh-line me-1"></i>
                      Retry
                    </button>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UserManagement;